import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../app";
import { groupItems } from "../utils/clustering";
import { calculateBoundingBoxForAnnotationGroup } from "../../annotator/helpers/pdfHelpers";
import { Rectangle } from "../../annotator/interfaces/textLayer";

type Props = {
  pageNumber: number;
  tokenIndices: Array<number> | null;
  isTempAnnotation: boolean;
  color: string;
  entityId: string;
  index: number;
};

type Response = {
  id: string;
  coords: Rectangle;
  rotation: number;
};

const getPdf = (state: RootState) => state.documentReducer.pdf;
const getScale = (state: RootState) => state.annotationReducer.scale;
const getPageNumber = (_: RootState, props: Props) => props.pageNumber;
const getTokenIndices = (_: RootState, props: Props) => props.tokenIndices;
const getEntityId = (_: RootState, props: Props) => props.entityId;
const getIndex = (_: RootState, props: Props) => props.index;

// Cache for per-annotation geometry (this rarely changes)
const geometryCache = new Map<string, Array<Response>>();
const MAX_CACHE_SIZE = 500;

export const clearGeometryCache = () => {
  geometryCache.clear();
};

// Selector that ONLY calculates geometry, not highlighting
const tokensGeometrySelector = createSelector(
  [getPdf, getScale, getPageNumber, getTokenIndices, getEntityId, getIndex],
  (
    pdf,
    scale,
    pageNumber,
    tokenIndices,
    entityId,
    index,
  ): Array<Response> | null => {
    if (!pdf?.pages?.length || !tokenIndices?.length) {
      return null;
    }

    const page = pdf.pages.find((p) => p.page === pageNumber);
    if (!page) {
      return null;
    }

    // Zooming dispatches `changeScale` and `rescalePdfPages` separately, so the
    // selector also runs while the scale is new but the tokens are not yet
    // rescaled. Keying on the page dimension (rescaled together with the
    // tokens) keeps that intermediate result from being served afterwards.
    const cacheKey = `${entityId}-${index}-${pageNumber}-${tokenIndices.join(
      ",",
    )}-${scale}-${page.dimension.width}x${page.dimension.height}`;

    // Check cache
    if (geometryCache.has(cacheKey)) {
      return geometryCache.get(cacheKey)!;
    }

    const foundTokensForIndices = page.tokens.filter((t) =>
      tokenIndices.includes(t.dataI),
    );

    if (!foundTokensForIndices.length) {
      return null;
    }

    const groupedIndices = groupItems(foundTokensForIndices, page.tokens);

    const result = groupedIndices.map((gi) => {
      const id = gi.map((i) => i.dataI).join("");
      const calcCoords = calculateBoundingBoxForAnnotationGroup(gi, scale);

      return {
        id,
        coords: calcCoords,
        rotation: calcCoords.rotation,
      };
    });

    // Update cache
    geometryCache.set(cacheKey, result);

    // Limit cache size
    if (geometryCache.size > MAX_CACHE_SIZE) {
      const firstKey = geometryCache.keys().next().value;
      if (firstKey !== undefined) {
        geometryCache.delete(firstKey);
      }
    }

    return result;
  },
);

export default tokensGeometrySelector;
